<script setup>
import { i18n } from 'boot/i18n';
import {
  HrErrorBanner,
  HrInfoBanner,
  HrSegmentedBtn,
  HrSubtleBtn,
  HrTextField,
  HrWarningBanner,
} from 'src/design-system';
import { formatCalendarDate } from 'src/utils/DateUtils';
import { computed, ref, watch } from 'vue';

import { UNIT_BASIS_ORDER } from '../../domain/constants/unitBasis';
import { toCodeString } from '../../domain/services/codeString';
import {
  SERVICE_CODE_DRAFT_ERRORS,
  validateServiceCodeDraft,
} from '../../domain/services/serviceCodeDraft';
import CalendarDateField from './CalendarDateField.vue';

/**
 * The fields for a code the clinic bills that the catalog does not carry.
 *
 * A FORM, NOT A DIALOG, and that split is the whole reason this file exists. Defining a code is a
 * continuation of searching for one — the person is already in the picker, has already looked, and
 * has not found what they need — so it happens on the SAME surface rather than in a modal stacked
 * over it. `ServiceCodePickerDialog` owns the chrome and swaps its body between the steps; this owns
 * the fields and nothing else.
 *
 * VALIDATION IS THE DOMAIN'S AND THE MESSAGES ARE THE FIELDS' — the same arrangement
 * `EditCoverageDialog` uses. `validateServiceCodeDraft` returns error codes, each field carries the
 * `:rules` that turn the codes it owns into its own wording, and `HrForm` refuses to submit until
 * they pass. An earlier version of this collected the failures into one banner at the foot of the
 * dialog; it put the message a long way from the control, read as one run-on sentence, and gave a
 * screen reader no association between the two. A field says what is wrong with itself.
 *
 * THE UNIT BASIS IS A SEGMENTED BUTTON, NOT THE FILTER CHIPS THE MOCKUP DRAWS. M3 permits a
 * single-select chip set, so the chips were not a violation — but a filter chip is framed for
 * narrowing a set that is dynamic and legitimately empty, and this is a required, mutually exclusive
 * attribute that gets persisted. A chip is toggleable back to null, which a required field then has
 * to suppress, and five chips announce as five unrelated toggle buttons with nothing to say that
 * choosing one clears the others.
 *
 * `HrSegmentedBtn` is M3's control for two to five mutually exclusive options and there are exactly
 * five. It is a `radiogroup` of radios with a roving tabindex, arrow-key selection and Home/End —
 * all of which chips would have needed layered on by hand — and it carries its own `error` state,
 * so the invalid treatment is the component's rather than ours.
 *
 * THE SEGMENT LABELS DROP THE "per", and that is what makes them fit. Every option repeated the
 * same three characters, so the width went to the word the five answers have in common instead of
 * the part that tells them apart — five segments all reading "per…". The label above supplies the
 * preposition once; the segments answer it. `unitBasesChoice` exists for exactly this position, and
 * is why `unitBases` is left alone: "per hour" is right standing on its own in a table cell, and
 * wrong five times in a row under a question that already said "per".
 *
 * IT IS SPREAD, and that is what keeps it inside the dialog. M3 specifies a segmented button as one
 * connected track that neither wraps nor scrolls, so content-width segments have no way to fit a
 * container they exceed — they simply run past it. Spread divides the available width instead, and
 * the design system gives the SELECTED segment its own content width so the chosen value is the one
 * label that never truncates. The check is hidden on the design system's own advice: it widens the
 * selected segment as it arrives, and selection is already carried by the M3 pill.
 *
 * IT STILL IS NOT A FORM FIELD, so `HrForm` cannot collect it and no `:rules` hang off it. That is
 * the one bit of validation this form does by hand: the host says when submission was attempted,
 * and the error is drawn beside the control rather than by it.
 *
 * THE BASIS IS AN INPUT HERE AND NOWHERE ELSE. Every other code inherits its basis from the
 * published set it came from; there is no entry to inherit from, so the org asserts it. That is a
 * difference in who supplies the value at creation and in nothing else — it stays a code-truth
 * afterwards, immutable, and no treatment service may override it.
 */
defineOptions({ name: 'MintServiceCodeForm' });

const props = defineProps({
  /**
   * The search term the user had typed when they gave up looking. Prefills the code, because they
   * have already told us what they are after and making them type it twice is the surface asking
   * them to repeat themselves.
   */
  initialCode: {
    type: String,
    default: '',
  },
  /**
   * Managed codes already carrying the typed string. ADVISORY — the org may legitimately bill a code
   * we also govern, so this never blocks.
   * @type {Array<Object>}
   */
  managedMatches: {
    type: Array,
    default: () => ([]),
  },
  /**
   * The clinic's own code with this string AND this start date. BLOCKING — that pair is what the
   * document id collides on, so a second one cannot be written.
   */
  ownMatch: {
    type: Object,
    default: null,
  },
  /**
   * Every code of the clinic's own under this string, whatever its period. CONTEXT, NOT A REFUSAL:
   * a second period is legitimate — a funder revising what a code covers reuses the string, and the
   * periods may even overlap — so this is shown so the user can tell their revision from a mistake.
   * @type {Array<Object>}
   */
  ownMatches: {
    type: Array,
    default: () => ([]),
  },
  /**
   * Set by the host once the primary action has been pressed, so the unit basis can show its error.
   * Every other field is validated by `HrForm` and needs nothing from outside — see the docblock for
   * why a segmented button cannot be.
   */
  submitAttempted: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Fired whenever anything changes, with the draft when it is complete and null when it is not. */
  'update:draft': null,
  /** Fired when the user takes the advice and wants the managed code instead, with its id. */
  'use-managed': null,
  /** Fired when the user wants the code they already have, with its id. */
  'use-own': null,
});

const code = ref(props.initialCode);
const descriptor = ref('');
const unitBasis = ref(null);
const validFrom = ref(null);

/** What the code string will be stored as — the value every rule and the write agree on. */
const normalizedCode = computed(() => toCodeString(code.value));

const values = computed(() => ({
  code: code.value,
  descriptor: descriptor.value,
  unitBasis: unitBasis.value,
  validFrom: validFrom.value,
}));

const errors = computed(() => validateServiceCodeDraft(values.value));

const has = (error) => errors.value.includes(error);

const KEY = 'settings.billing.chargeMaster.mint';

/*
 * ONE RULE PER FAILURE, not one per field, so each says the specific thing that is wrong. Quasar
 * shows the first rule to fail, and they are ordered so the more specific reason wins: a code that
 * is too long has been typed, and telling that person it is "required" would be nonsense.
 */
const codeRules = [
  () => !has(SERVICE_CODE_DRAFT_ERRORS.CODE_TOO_LONG) || i18n.t(`${KEY}.errorCodeTooLong`),
  () => !has(SERVICE_CODE_DRAFT_ERRORS.CODE_UNSTORABLE) || i18n.t(`${KEY}.errorCodeUnstorable`),
  () => !has(SERVICE_CODE_DRAFT_ERRORS.CODE_REQUIRED) || i18n.t(`${KEY}.errorCodeEmpty`),
];

const descriptorRules = [
  () => !has(SERVICE_CODE_DRAFT_ERRORS.DESCRIPTOR_REQUIRED)
    || i18n.t(`${KEY}.errorDescriptorRequired`),
];

// VALID_FROM_UNREADABLE cannot happen through the current picker; it is the guard against a date
// input that speaks another type being swapped in, and it is surfaced rather than swallowed for the
// same reason `EditCoverageDialog` surfaces its own.
const validFromRules = [
  () => !has(SERVICE_CODE_DRAFT_ERRORS.VALID_FROM_UNREADABLE)
    || i18n.t(`${KEY}.errorValidFromUnreadable`),
  () => !has(SERVICE_CODE_DRAFT_ERRORS.VALID_FROM_REQUIRED)
    || i18n.t(`${KEY}.errorValidFromRequired`),
];

/**
 * The segmented button is not a form field, so its error waits for the action to be pressed.
 *
 * `lazy-rules` gives every other control that restraint for free — nothing shouts at somebody for
 * arriving — and this is what buys the same here.
 */
const unitBasisError = computed(() => (
  props.submitAttempted && has(SERVICE_CODE_DRAFT_ERRORS.UNIT_BASIS_REQUIRED)
    ? i18n.t(`${KEY}.errorUnitBasisRequired`)
    : null
));

const unitBasisOptions = computed(() => UNIT_BASIS_ORDER.map((basis) => ({
  value: basis,
  label: i18n.t(`unitBasesChoice.${basis}`),
})));

/**
 * The draft when it can be written, and null otherwise.
 *
 * THE DUPLICATE COUNTS AS INCOMPLETE, even though it is not a fault in any field. A clinic holds one
 * record per code string, so with `ownMatch` set there is nothing a valid form could produce — and
 * the banner beside the code field is where that is said, because it carries the way out.
 */
/** Periods the clinic already holds under this string, other than the one being blocked on. */
const otherPeriods = computed(() => props.ownMatches
  .filter((match) => match.id !== props.ownMatch?.id)
  .map((match) => formatCalendarDate(match.validFrom))
  .join(', '));

const draft = computed(() => (errors.value.length > 0 || props.ownMatch !== null ? null : {
  code: normalizedCode.value,
  descriptor: descriptor.value.trim(),
  unitBasis: unitBasis.value,
  validFrom: validFrom.value,
}));

watch(
  [draft, normalizedCode, validFrom],
  () => emit('update:draft', {
    draft: draft.value,
    code: code.value,
    validFrom: validFrom.value,
  }),
  { immediate: true },
);
</script>

<template>
  <!--
    THE APP'S STANDARD FORM GRID — `row` + `q-col-gutter-md` + `col-*`, as EditCoverageDialog uses.
    Everything about the spacing and the breakpoint is Quasar's: `q-col-gutter` pads the columns and
    compensates on the container, so nothing sits inboard of the shared left edge, and `col-sm-6`
    gives the one-column phone layout without a media query of our own.

    NOT `q-gutter-md`, which is the margin-based sibling. Applied to a bare `fieldset` it pushed the
    element off the edge every other control aligned to — the misalignment this form had.

    THE TWO SHORT FIELDS SHARE THE TOP ROW, which is what stops the form being half-empty down its
    right side. It reorders the fields against the mockup (code, in-effect-from, unit basis,
    description); the DOM order is the visual order, so tab order follows the eye rather than
    fighting it.
  -->
  <div class="row q-col-gutter-md">
    <p class="col-12 q-mb-none hr-text-on-surface-variant">
      {{ i18n.t('settings.billing.chargeMaster.mint.textCaption') }}
    </p>

    <hr-text-field
      v-model="code"
      class="col-12 col-sm-6"
      :label="i18n.t('settings.billing.chargeMaster.mint.inputCode')"
      :hint="i18n.t('settings.billing.chargeMaster.mint.hintCode')"
      :rules="codeRules"
      lazy-rules
      autofocus
      data-e2e="input-mint-code"
    />

    <!--
      NO DEFAULT DATE, and the empty field is the design rather than an omission. Prefilling today
      looks helpful and is the one value that is almost always wrong: a code the funder has been
      billing for months would silently refuse every service delivered before this afternoon, and
      nothing on any screen would say why. An empty required field asks a question somebody can
      answer; a wrong default answers it for them, invisibly.

      What the hint used to add — that earlier services cannot be billed — now sits on the review
      step, where the date is restated and vouched for.
    -->
    <calendar-date-field
      v-model="validFrom"
      class="col-12 col-sm-6"
      :label="i18n.t('settings.billing.chargeMaster.mint.inputValidFrom')"
      :hint="i18n.t('settings.billing.chargeMaster.mint.hintValidFrom')"
      :rules="validFromRules"
      lazy-rules
      data-e2e="input-mint-valid-from"
    />

    <!--
      BLOCKING, SO IT IS AN ERROR AND NOT A WARNING. The clinic already has this code and a second
      record cannot exist, so the only way forward is the one this offers — go to the code they have.
    -->
    <hr-error-banner
      v-if="props.ownMatch !== null"
      class="col-12"
      :message="i18n.t('settings.billing.chargeMaster.mint.errorDuplicate', {
        code: props.ownMatch.code,
        descriptor: props.ownMatch.descriptor,
      })"
      multi-line
      data-e2e="banner-duplicate-code"
    >
      <template #action>
        <hr-subtle-btn
          :label="i18n.t('settings.billing.chargeMaster.mint.buttonUseExisting')"
          data-e2e="button-use-existing-code"
          @click="emit('use-own', props.ownMatch.id)"
        />
      </template>
    </hr-error-banner>

    <!--
      A SECOND PERIOD IS LEGITIMATE, so this informs rather than warns. A funder that revises what a
      code covers reuses the string for a new period, and the periods may even overlap — none of that
      is ours to refuse. What the user needs is to see what they already hold under this string, so
      they can tell their own revision from their own mistake.
    -->
    <hr-info-banner
      v-if="otherPeriods.length > 0"
      class="col-12"
      :message="i18n.t('settings.billing.chargeMaster.mint.noticeOtherPeriods', {
        code: normalizedCode,
        dates: otherPeriods,
      })"
      data-e2e="banner-other-periods"
    />

    <!--
      ADVICE, NOT AN ERROR, so it sits in a warning banner rather than under the field in the error
      role. The field is not wrong — the code may be exactly what the funder bills — and coloring it
      invalid would tell the user to change something when the honest message is "check first".
    -->
    <hr-warning-banner
      v-if="props.managedMatches.length > 0"
      class="col-12"
      :message="i18n.t('settings.billing.chargeMaster.mint.warningAlreadyGoverned', {
        codes: props.managedMatches.map((match) => `${match.code} — ${match.descriptor}`).join('; '),
      })"
      multi-line
      data-e2e="banner-shadow-code"
    >
      <template #action>
        <hr-subtle-btn
          :label="i18n.t('settings.billing.chargeMaster.mint.buttonUseOurs')"
          data-e2e="button-use-managed-code"
          @click="emit('use-managed', props.managedMatches[0].id)"
        />
      </template>
    </hr-warning-banner>

    <!-- No hint: the label says what the field is, and "in your own words" restated the obvious. -->
    <hr-text-field
      v-model="descriptor"
      class="col-12"
      :label="i18n.t('settings.billing.chargeMaster.mint.inputDescriptor')"
      :rules="descriptorRules"
      lazy-rules
      data-e2e="input-mint-descriptor"
    />
    <!--
      A VISIBLE LABEL *AND* AN `aria-label`, both from the same key. The question has to be readable
      as well as announced, since five bare durations do not imply what is being asked — but the
      control is not a form field, so there is no label element to associate. Naming the group
      directly beats minting a document id to point `aria-labelledby` at: the id has to be unique per
      instance, and every scheme for that is either a counter in the wrong scope or a Vue 3.5 API the
      declared version range does not promise. Two reads of one i18n key cannot drift.

      The visible copy is then `aria-hidden`, or a screen reader hears the question twice — once as
      the group's name and once as loose text above it.
    -->
    <div class="col-12 q-pb-md">
      <p
        class="hr-body-small hr-text-on-surface-variant q-mb-sm"
        aria-hidden="true"
      >
        {{ i18n.t('settings.billing.chargeMaster.mint.inputUnitBasis') }}
      </p>

      <!--
        `spread` IS WHAT KEEPS IT INSIDE THE DIALOG. Content-width segments have no way to fit a
        container they exceed: M3 specifies a segmented button as one connected track that neither
        wraps nor scrolls, so an unspread control simply runs past its parent. Spread divides the
        available width instead, and M3's own answer to a label that then does not fit is the
        ellipsis the component already implements.
      -->
      <hr-segmented-btn
        v-model="unitBasis"
        :options="unitBasisOptions"
        :error="unitBasisError !== null"
        spread
        hide-selected-check
        :aria-label="i18n.t('settings.billing.chargeMaster.mint.inputUnitBasis')"
        data-e2e="select-unit-basis"
      />

      <!--
        `role="alert"` is the part a field's own message gets for free and this has to ask for. The
        control carries the invalid color through its `error` prop; the sentence still has to exist,
        because a color alone says something is wrong without saying what.
      -->
      <p
        v-if="unitBasisError !== null"
        class="hr-body-small hr-text-error q-mt-sm q-mb-none"
        role="alert"
        data-e2e="error-unit-basis"
      >
        {{ unitBasisError }}
      </p>
    </div>
  </div>
</template>
